import { useState, useLayoutEffect, useMemo, useRef } from "react";

import Chip from "core/components/Chip";
import Typography from "core/components/Typography";
import styled from "styled-components";
import Search from "../Search";
import { EmptyCheckbox, StyledOption } from "../Combobox/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckSquare } from "@fortawesome/pro-solid-svg-icons";
import Button from "core/components/Button";
import Field from "../Field";

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ResultsContainer = styled.div`
  --results-container-padding: 36px;
  --group-row-height: 34px;
  --group-count: ${(props) => props.groupCount ?? 10};
  --group-container-height: calc(var(--group-count) * var(--group-row-height));
  --group-total-margin: calc((var(--group-count) - 1) * 4px);

  height: calc(
    var(--group-container-height) + var(--group-total-margin) +
      var(--results-container-padding)
  );
  width: 100%;
  display: flex;
  gap: 16px;
  padding-bottom: 18px;
`;

const Separator = styled.div`
  width: 1px;
  background-color: #d3d3d3;
`;

const GroupContainer = styled.div`
  height: fit-content;
  width: fit-content;
`;

const Group = styled.div`
  min-width: max-content;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;
  cursor: pointer;
  padding: 5px 5px 5px 9px;
  border-radius: 5px;

  &:not(:last-child) {
    margin-bottom: 4px;
  }

  &:hover {
    background-color: #ddcdef;
  }

  ${(props) =>
    props.isActive &&
    `
    background-color: #DDCDEF;
    `}

  ${(props) =>
    props.isAllGroup &&
    `
      font-weight: 600;
  `}

  ${(props) =>
    props.hasNoOptions &&
    `
      opacity: .5;
  `}
`;

const OptionsContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

const FaderContainer = styled.div`
  position: relative;
  flex-grow: 1;
`;

const OptionsFaderTop = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: calc(100% - 15px);
  height: 50px;
  background: linear-gradient(hsl(0deg 0% 100% / 100%), hsl(0deg 0% 100% / 0%));

  opacity: 0;
  z-index: -1;
  transition: opacity 150ms linear;
  &[data-visible="true"] {
    opacity: 1;
    z-index: 10;
  }
`;

const OptionsFaderBottom = styled.div`
  position: absolute;
  bottom: 0px;
  left: 0px;
  width: calc(100% - 15px);
  height: 50px;
  background: linear-gradient(hsl(0deg 0% 100% / 0%), hsl(0deg 0% 100% / 100%));
  z-index: 10;
`;

const Option = styled(StyledOption)`
  background-color: unset;
  border-radius: 5px;
  padding-top: 7px;
  padding-bottom: 7px;
  padding-left: 12px;
  padding-right: 12px;
  user-select: none;

  svg {
    width: 20px;
    height: 20px;
  }

  ${EmptyCheckbox} {
    width: 20px;
    height: 20px;
  }

  &:not(:last-child) {
    margin-bottom: 4px;
  }

  &:last-child {
    margin-bottom: 34px;
  }
`;

const PlaceholderOption = styled(Option)`
  pointer-events: none;
  font-size: 1rem !important;
`;

const MassiveMultiSelect = ({
  id = "",
  name = "",
  placeholder = "Search...",
  options: controlledOptions = [],
  isLoading = false,
  isDisabled = false,
  autoFocus = false,
  value = [],
  onChange = null,
  error = "",
}) => {
  const optionsContainerRef = useRef();
  const [searchValue, setSearchValue] = useState("");
  const [activeGroupId, setActiveGroupId] = useState("__all");
  const [showTopFader, setShowTopFader] = useState(false);

  const groups = useMemo(() => {
    return controlledOptions.map((fieldGroup) => ({
      value: fieldGroup.groupId,
      label: fieldGroup.label,
    }));
  }, [controlledOptions]);

  const options = useMemo(() => {
    return controlledOptions.reduce((options, fieldGroup) => {
      const childOptionsWithGroupId = fieldGroup.children.map((child) => {
        child.groupId = fieldGroup.groupId;
        return child;
      });
      return options.concat(childOptionsWithGroupId);
    }, []);
  }, [controlledOptions]);

  const selectedValues = useMemo(() => {
    return value ?? [];
  }, [value]);

  const selectedOptions = useMemo(() => {
    return selectedValues.map((v) => options.find((o) => o.value === v));
  }, [options, selectedValues]);

  const addSelectedValue = (value) => {
    onChange(selectedValues.concat(value));
  };

  const removeSelectedValue = (value) => {
    onChange(selectedValues.filter((v) => v !== value));
  };

  const selectAllInGroup = (groupId) => {
    const allGroupOptions = options.filter(
      (option) => option.groupId === groupId
    );
    const allGroupValues = allGroupOptions.map((option) => option.value);

    onChange(
      selectedValues
        .filter((v) => !allGroupValues.includes(v))
        .concat(allGroupValues)
    );
  };

  const removeAllInGroup = (groupId) => {
    const allGroupOptions = options.filter(
      (option) => option.groupId === groupId
    );
    const allGroupValues = allGroupOptions.map((option) => option.value);

    onChange(selectedValues.filter((v) => !allGroupValues.includes(v)));
  };

  useLayoutEffect(() => {
    if (optionsContainerRef?.current) {
      optionsContainerRef.current.scrollTo(0, 0);
    }
  }, [activeGroupId]);

  const activeOptions = useMemo(() => {
    if (activeGroupId === "__all") {
      return selectedOptions.filter((option) => {
        return option.label
          .toLowerCase()
          .includes(searchValue.trim().toLowerCase());
      });
    } else {
      return options
        .filter((option) => option.groupId === activeGroupId)
        .filter((option) => {
          return option.label
            .toLowerCase()
            .includes(searchValue.trim().toLowerCase());
        });
    }
  }, [activeGroupId, searchValue, options, selectedOptions]);

  return (
    <Container>
      <Field
        type="search"
        id={id}
        name={name}
        value={searchValue}
        onChange={setSearchValue}
        placeholder={placeholder}
        autoFocus={autoFocus}
        error={error}
        isDisabled={isDisabled}
        showSkeleton={isLoading}
        styles={{
          container: { width: "100%" },
          input: {
            border: "1px solid var(--input-border)",
          },
          cancelButton: {},
          icon: {},
        }}
      />
      <ResultsContainer groupCount={groups.length + 1}>
        <GroupContainer>
          {[
            {
              value: "__all",
              label: `All Selected`,
            },
          ]
            .concat(groups)
            .map((group) => {
              const isAllGroup = group.value === "__all";
              const selectedGroupCount = isAllGroup
                ? selectedOptions.length
                : selectedOptions.reduce((total, current) => {
                    if (current.groupId === group.value) {
                      return total + 1;
                    }

                    return total;
                  }, 0);
              const hasNoOptions =
                !isAllGroup &&
                options
                  .filter((option) => option.groupId === group.value)
                  .filter((option) => {
                    return option.label
                      .toLowerCase()
                      .includes(searchValue.trim().toLowerCase());
                  }).length === 0;

              return (
                <Group
                  key={group.value}
                  tabIndex={0}
                  isActive={group.value === activeGroupId}
                  isAllGroup={isAllGroup}
                  hasNoOptions={hasNoOptions}
                  onClick={() => {
                    setShowTopFader(false);
                    setActiveGroupId(group.value);
                  }}
                >
                  <Typography variant="p" noMargin color="#2b1850">
                    {group.label}
                  </Typography>
                  <div
                    style={{
                      opacity: selectedGroupCount > 0 ? 1 : 0,
                    }}
                  >
                    <Chip text={selectedGroupCount} />
                  </div>
                </Group>
              );
            })}
        </GroupContainer>
        <Separator />
        <FaderContainer>
          <OptionsFaderTop data-visible={showTopFader} />
          <OptionsFaderBottom />
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSearchValue("");
            }}
            style={{
              display: !!searchValue ? "block" : "none",
              position: "absolute",
              bottom: -40,
              right: 15,
              zIndex: 10,
            }}
          >
            Close Search
          </Button>

          <OptionsContainer
            ref={optionsContainerRef}
            onScroll={(event) => {
              if (event.target.scrollTop === 0) {
                setShowTopFader(false);
              }

              if (event.target.scrollTop > 0) {
                setShowTopFader(true);
              }
            }}
          >
            {activeOptions.length === 0 && (
              <PlaceholderOption>
                No options{" "}
                {activeGroupId === "__all" ? "selected" : "available"}
              </PlaceholderOption>
            )}
            {(activeGroupId === "__all" || !!searchValue
              ? []
              : [
                  {
                    value: "__select_all",
                    label: "Select All",
                    groupId: activeGroupId,
                  },
                ]
            )
              .concat(activeOptions)
              .map((option) => {
                const isSelectAllOption = option.value === "__select_all";
                const isSelected = selectedValues.includes(option.value);
                const allGroupOptions = options.filter(
                  (o) => o.groupId === option.groupId
                );
                const isAllSelected =
                  selectedOptions.filter((o) => o.groupId === option.groupId)
                    .length === allGroupOptions.length;

                return (
                  <Option
                    key={option.value}
                    onClick={(event) => {
                      if (isSelectAllOption) {
                        if (isAllSelected) {
                          removeAllInGroup(option.groupId);
                        } else {
                          selectAllInGroup(option.groupId);
                        }
                      } else {
                        if (isSelected) {
                          removeSelectedValue(option.value);
                        } else {
                          addSelectedValue(option.value);
                        }
                      }
                    }}
                  >
                    {isSelected || isAllSelected ? (
                      <FontAwesomeIcon icon={faCheckSquare} />
                    ) : (
                      <EmptyCheckbox />
                    )}
                    <div data-option-value={option.value}>{option.label}</div>
                  </Option>
                );
              })}
          </OptionsContainer>
        </FaderContainer>
      </ResultsContainer>
    </Container>
  );
};

export default MassiveMultiSelect;
